import { Component } from '@theme/component';

const MINUTE = 60 * 1000;

/**
 * @typedef {Object} BuyBoxGalleryRefs
 * @property {HTMLElement} track - Scroll-snap slide track
 * @property {HTMLButtonElement[]} [thumbs] - Desktop thumbnail buttons
 * @property {HTMLElement[]} [dots] - Mobile position dots
 */

/**
 * Gallery for the 7 Reasons Why buy box. Slides live in one scroll-snap track:
 * swiped on mobile, driven by thumbnails on desktop.
 *
 * @extends {Component<BuyBoxGalleryRefs>}
 */
class BuyBoxGallery extends Component {
  requiredRefs = ['track'];

  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.track.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track.removeEventListener('scroll', this.#handleScroll);
    cancelAnimationFrame(this.#frame);
  }

  /**
   * @param {number} index - Slide index
   */
  select(index) {
    const { track } = this.refs;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollTo({ left: index * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const { track } = this.refs;
      if (!track.clientWidth) return;

      this.#setActive(Math.round(track.scrollLeft / track.clientWidth));
    });
  };

  /**
   * @param {number} activeIndex
   */
  #setActive(activeIndex) {
    for (const [index, thumb] of (this.refs.thumbs ?? []).entries()) {
      thumb.setAttribute('aria-current', String(index === activeIndex));
    }

    for (const [index, dot] of (this.refs.dots ?? []).entries()) {
      dot.classList.toggle('srw-buy-box__dot--active', index === activeIndex);
    }
  }
}

/**
 * @typedef {Object} DeliveryEstimateRefs
 * @property {HTMLElement[]} messages - Elements whose data-template gets [time] and [date] filled in
 */

/**
 * Countdown to the same-day shipping cutoff plus an estimated delivery date.
 * Weekends are skipped; holidays are not. Stays hidden if the time zone is invalid.
 *
 * @extends {Component<DeliveryEstimateRefs>}
 */
class DeliveryEstimate extends Component {
  requiredRefs = ['messages'];

  /** @type {number | undefined} */
  #interval;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#interval = window.setInterval(() => this.#render(), MINUTE);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#interval);
  }

  #render() {
    const cutoffHour = Number(this.dataset.cutoffHour);
    const transitDays = Number(this.dataset.transitDays);
    const now = getZonedNow(this.dataset.timeZone || 'America/New_York');

    if (!now || Number.isNaN(cutoffHour) || Number.isNaN(transitDays)) return;

    const shipDate = new Date(now);
    shipDate.setUTCHours(cutoffHour, 0, 0, 0);

    if (!isBusinessDay(shipDate) || now >= shipDate) {
      do {
        shipDate.setUTCDate(shipDate.getUTCDate() + 1);
      } while (!isBusinessDay(shipDate));
    }

    const deliveryDate = new Date(shipDate);
    let businessDays = 0;

    while (businessDays < transitDays) {
      deliveryDate.setUTCDate(deliveryDate.getUTCDate() + 1);
      if (isBusinessDay(deliveryDate)) businessDays += 1;
    }

    const time = formatDuration(Math.ceil((shipDate.getTime() - now.getTime()) / MINUTE));
    const date = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(deliveryDate);

    for (const message of this.refs.messages) {
      message.textContent = (message.dataset.template ?? '').replace('[time]', time).replace('[date]', date);
    }

    this.hidden = false;
  }
}

/**
 * Wall-clock time in a time zone, stored in the UTC fields of a Date so day math ignores the visitor's zone.
 * @param {string} timeZone - IANA time zone name
 * @returns {Date | null}
 */
const getZonedNow = (timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(new Date());

    /** @param {Intl.DateTimeFormatPartTypes} type */
    const part = (type) => Number(parts.find((entry) => entry.type === type)?.value);

    return new Date(
      Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'))
    );
  } catch (error) {
    console.error('[DeliveryEstimate] Invalid time zone', error);
    return null;
  }
};

/**
 * @param {Date} date
 * @returns {boolean}
 */
const isBusinessDay = (date) => {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6;
};

/**
 * @param {number} totalMinutes
 * @returns {string} For example "4h 12m", or "2d 6h" past a day
 */
const formatDuration = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

if (!customElements.get('srw-buy-box-gallery')) {
  customElements.define('srw-buy-box-gallery', BuyBoxGallery);
}

if (!customElements.get('srw-delivery-estimate')) {
  customElements.define('srw-delivery-estimate', DeliveryEstimate);
}
